import React, { useEffect, useState } from "react";
import {
    Paper,
    Skeleton,
    Typography,
    Box,
    Chip,
    Divider,
    Button,
    Grid,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DownloadIcon from '@mui/icons-material/Download';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VerifiedIcon from '@mui/icons-material/Verified';
import { usePurchaseContractsController } from "./context/purchase-contracts-controller";
import { usePurchaseContractsState } from "./context/purchase-contracts-context";
import PageHeader from "../components/shared/page-header";
import { useParams } from "react-router-dom";
import { useNavigate } from "../../hooks/useNavigate";
import dayjs from "dayjs";

export function PurchaseContractDetailComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const { loadDetail, loadFiles } = usePurchaseContractsController();
    const { contractDetail, contractFiles } = usePurchaseContractsState();
    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            if (id) {
                await loadDetail(Number(id));
                await loadFiles(Number(id));
                setInitialLoading(false);
            }
        };
        fetchData();
    }, [id]);

    useEffect(() => {
        if (contractDetail?.externalUrl && !initialLoading) {
            window.open(contractDetail.externalUrl, '_blank', 'noopener,noreferrer');
            navigate('/contratos-aquisicoes');
        }
    }, [contractDetail, initialLoading, navigate]);

    const handleBack = () => {
        navigate('/contratos-aquisicoes');
    };

    const handleDownload = (url: string) => {
        if (url) {
            window.open(url, '_blank');
        }
    };

    const formatCNPJ = (cnpj: string) => {
        if (!cnpj) return '-';
        const cleaned = cnpj.replace(/\D/g, '');
        return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    return (
        <Box>
            <PageHeader
                title={contractDetail?.contractedCompanyName || 'Carregando...'}
                subtitle=""
                breadcrumbs={[
                    { label: 'Contratos e Aquisições', href: '/contratos-aquisicoes' },
                    { label: contractDetail?.contractedCompanyName || 'Carregando...' }
                ]}
            />

            {initialLoading ? (
                <Box>
                    <Skeleton variant="rounded" width="100%" height={400} sx={{ borderRadius: 2, mb: 3 }} />
                    <Skeleton variant="text" width="60%" height={60} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="80%" height={30} />
                </Box>
            ) : (
                <React.Fragment>
                    {(contractDetail?.isCovid19 || contractDetail?.isPncp) && (
                        <Box sx={{ mb: 3, display: 'flex', gap: 1 }}>
                            {contractDetail?.isPncp && (
                                <Chip
                                    icon={<VerifiedIcon />}
                                    label="PNCP"
                                    color="success"
                                    size="medium"
                                    sx={{ fontWeight: 600 }}
                                />
                            )}
                            {contractDetail?.isCovid19 && (
                                <Chip
                                    label="Contrato relacionado ao COVID-19"
                                    color="warning"
                                    size="medium"
                                    sx={{ fontWeight: 600 }}
                                />
                            )}
                        </Box>
                    )}

                    <Paper sx={{ p: 4, mb: 3 }} elevation={2}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                            Informações do Contrato
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <BusinessIcon color="primary" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Empresa Contratada
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {contractDetail?.contractedCompanyName || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <DescriptionIcon color="primary" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            CNPJ
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {formatCNPJ(contractDetail?.contractedCompanyDocument || '')}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <DescriptionIcon color="primary" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Número do Processo
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {contractDetail?.processNumber || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <CalendarTodayIcon color="primary" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Prazo do Contrato
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {contractDetail?.contractDeadline
                                                ? dayjs(contractDetail.contractDeadline).format('DD/MM/YYYY')
                                                : '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {contractDetail?.contractValue !== undefined && (
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                        <AttachMoneyIcon color="primary" />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Valor do Contrato
                                            </Typography>
                                            <Typography variant="body1" fontWeight={500}>
                                                {formatCurrency(contractDetail.contractValue)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            )}

                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <AttachMoneyIcon color="primary" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Valor Pago
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {formatCurrency(contractDetail?.amountPaid || 0)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <AttachMoneyIcon color="primary" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Saldo
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {formatCurrency(contractDetail?.balance || 0)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {contractDetail?.description && (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                        Descrição
                                    </Typography>
                                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                                        {contractDetail.description}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>

                    {contractFiles && contractFiles.length > 0 && (
                        <Paper sx={{ p: 4, mb: 3 }} elevation={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                <AttachFileIcon color="primary" />
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                    Documentos do Contrato ({contractFiles.length})
                                </Typography>
                            </Box>

                            <Grid container spacing={2}>
                                {contractFiles.map((file, index) => (
                                    <Grid item xs={12} key={index}>
                                        <Paper
                                            sx={{
                                                p: 2,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                backgroundColor: 'background.default',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover',
                                                    transform: 'translateX(4px)',
                                                }
                                            }}
                                            elevation={0}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                                                <DescriptionIcon color="primary" />
                                                <Box>
                                                    <Typography variant="body1" fontWeight={500}>
                                                        {file.title || `Documento ${file.sequence}`}
                                                    </Typography>
                                                    {file.typeDocumentName && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {file.typeDocumentName}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                            <Button
                                                variant="outlined"
                                                startIcon={<DownloadIcon />}
                                                onClick={() => handleDownload(file.url)}
                                                size="small"
                                            >
                                                Baixar
                                            </Button>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                            size="large"
                        >
                            Voltar para Contratos e Aquisições
                        </Button>
                    </Box>
                </React.Fragment>
            )}
        </Box>
    );
}
